import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { CardInstanceId } from '@hr-outsourcing/shared';
import { EntityId } from '@/types/common';
interface CardUIState {
  isExpanded: boolean;
  isLoading: boolean;
  patches: Record<string, unknown>;
}
interface UndoAction {
  id: string;
  type: string;
  timestamp: number;
  undo: () => void;
  description: string;
}
interface ChatUIStateSlice {
  cards: Record<string, CardUIState>;
  undoStack: UndoAction[];
  maxUndoSize: number;
  editingMessageId: EntityId | null;
  editingContent: string;
}
interface ChatUIActions {
  getCardState: (cardId: CardInstanceId) => CardUIState;
  setCardExpanded: (cardId: CardInstanceId, expanded: boolean) => void;
  setCardLoading: (cardId: CardInstanceId, loading: boolean) => void;
  patchCard: (cardId: CardInstanceId, patch: Record<string, unknown>) => void;
  pushUndo: (action: Omit<UndoAction, 'id' | 'timestamp'>) => void;
  undo: () => UndoAction | undefined;
  clearUndo: () => void;
  setEditingMessage: (messageId: EntityId | null, content?: string) => void;
  reset: () => void;
}
type ChatUIStore = ChatUIStateSlice & ChatUIActions;
const DEFAULT_CARD_STATE: CardUIState = {
  isExpanded: false,
  isLoading: false,
  patches: {},
};
export const useChatUIStore = create<ChatUIStore>()(
  devtools(
    (set, get) => ({
      cards: {},
      undoStack: [],
      maxUndoSize: 20,
      editingMessageId: null,
      editingContent: '',
      getCardState: (cardId) => {
        return get().cards[cardId] ?? DEFAULT_CARD_STATE;
      },
      setCardExpanded: (cardId, expanded) => {
        set((state) => ({
          cards: {
            ...state.cards,
            [cardId]: { ...(state.cards[cardId] ?? DEFAULT_CARD_STATE), isExpanded: expanded },
          },
        }));
      },
      setCardLoading: (cardId, loading) => {
        set((state) => ({
          cards: {
            ...state.cards,
            [cardId]: { ...(state.cards[cardId] ?? DEFAULT_CARD_STATE), isLoading: loading },
          },
        }));
      },
      patchCard: (cardId, patch) => {
        set((state) => ({
          cards: {
            ...state.cards,
            [cardId]: {
              ...(state.cards[cardId] ?? DEFAULT_CARD_STATE),
              patches: { ...(state.cards[cardId]?.patches ?? {}), ...patch },
            },
          },
        }));
      },
      pushUndo: (action) => {
        set((state) => {
          const newAction: UndoAction = {
            ...action,
            id: `undo_${Date.now()}_${Math.random().toString(36).slice(2)}`,
            timestamp: Date.now(),
          };
          const newStack = [newAction, ...state.undoStack].slice(0, state.maxUndoSize);
          return { undoStack: newStack };
        });
      },
      undo: () => {
        const { undoStack } = get();
        if (undoStack.length === 0) return undefined;
        const [action, ...rest] = undoStack;
        action.undo();
        set({ undoStack: rest });
        return action;
      },
      clearUndo: () => set({ undoStack: [] }),
      setEditingMessage: (messageId, content) => {
        set({ editingMessageId: messageId, editingContent: content ?? '' });
      },
      reset: () =>
        set({
          cards: {},
          undoStack: [],
          editingMessageId: null,
          editingContent: '',
        }),
    }),
    { name: 'ChatUIStore', enabled: import.meta.env.DEV }
  )
);
